import React, { useState, useEffect } from 'react';
import api from './api';
import {
  couleurs, espace, rayon, texte, carte as carteStyle,
  bouton, badge, bandeau, tableau,
} from './theme';

/* Indicateur clé. Trois lignes, toujours dans le même ordre :
   l'étiquette (ce que c'est), la valeur (le chiffre), le contexte
   (ce qu'il vaut par rapport à quoi). C'est cette troisième ligne qui
   manquait : « 1 284 » ne dit rien, « 1 284 · 26 % du parc » se lit.

   La valeur reste en encre normale, jamais colorée : cinq chiffres de
   cinq couleurs différentes, c'est un arc-en-ciel où l'anomalie ne se
   distingue plus. Seul `ton` — passé uniquement quand il y a vraiment
   quelque chose à signaler — colore l'indicateur. */
function Indicateur({ etiquette, valeur, contexte, ton }) {
  const alerte = ton === 'danger' || ton === 'alerte';
  return (
    <div style={{
      ...styles.carte,
      borderColor: alerte ? couleurs[ton] : couleurs.bord,
    }}>
      <p style={styles.carteTitre}>{etiquette}</p>
      <p style={{ ...styles.carteValeur, color: alerte ? couleurs[ton] : couleurs.encre }}>
        {valeur ?? '—'}
      </p>
      {contexte && <p style={styles.carteContexte}>{contexte}</p>}
    </div>
  );
}

/* Barre de proportion. Remplace les demi-cercles décoratifs : une
   barre horizontale se compare d'une ligne à l'autre sans effort. */
function Barre({ pct, ton = 'accent', hauteur = 6 }) {
  return (
    <div style={{ background: couleurs.surfaceDouce, borderRadius: rayon.sm,
                  height: hauteur, overflow: 'hidden' }}>
      <div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: '100%',
                    background: couleurs[ton] || ton, borderRadius: rayon.sm,
                    transition: 'width 400ms ease' }} />
    </div>
  );
}

// ─── STATISTIQUES ──────────────────────────────────────────

/* Durée en secondes → « 1 min 25 s ». Un barman ne compte pas en
   secondes au-delà de la minute. */
function duree(s) {
  if (s === null || s === undefined) return '—';
  if (s < 60) return `${Math.round(s)} s`;
  return `${Math.floor(s / 60)} min ${String(Math.round(s % 60)).padStart(2, '0')} s`;
}

const euros = (v) => (v === null || v === undefined ? '—'
  : `${Number(v).toFixed(2).replace('.', ',')} €`);

/* Durée d'activité du festival, et la plage en clair. Deux fonctions
   plutôt qu'une : le chiffre va dans l'indicateur, la plage horaire
   dans sa ligne de contexte. */
function heures(debut, fin) {
  if (!debut || !fin) return '—';
  const h = (new Date(fin) - new Date(debut)) / 3600000;
  return h < 1 ? `${Math.round(h * 60)} min` : `${h.toFixed(1).replace('.', ',')} h`;
}
function plage(debut, fin) {
  if (!debut || !fin) return 'aucune vente enregistrée';
  const f = (d) => new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return `de ${f(debut)} à ${f(fin)}`;
}

/* Trois barres CÔTE À CÔTE pour une heure : servis, rendus, perdus.

   Elles étaient empilées, et c'était faux — corrigé le 2026-08-29.
   Empiler signifie « voici la décomposition d'un tout ». Or les trois
   séries ne parlent pas de la même population : un gobelet servi à
   22 h revient vers minuit, donc les retours de minuit sont les ventes
   de 22 h. Ce sont trois flux indépendants dans le temps.

   L'erreur avait deux conséquences visibles : le segment « encore
   dehors » se calculait comme sorties − retours − pertes DANS LA MÊME
   HEURE, une soustraction de quantités sans rapport qui devenait
   négative en fin de nuit ; et aux heures sans vente, la barre des
   retours dépassait le cadre et se faisait couper.

   L'échelle porte désormais sur le maximum des TROIS séries, sans quoi
   les heures de retours sans ventes sortaient du graphique. */
function ColonneFlux({ h, max }) {
  const heure = (h.heure || '').slice(11, 16);
  const barres = [
    ['accent', h.sorties, 'servis'],
    ['succes', h.retours, 'rendus'],
    ['danger', h.pertes, 'perdus'],
  ];
  return (
    <div style={{ flex: '1 0 46px', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', gap: 4 }}
         title={`${heure} — ${h.sorties} servis, ${h.retours} rendus, ${h.pertes} perdus`}>
      <span style={{ fontSize: 10.5, color: couleurs.encreDouce,
                     fontVariantNumeric: 'tabular-nums' }}>{h.sorties}</span>
      <div style={{ width: '100%', height: 130, display: 'flex', alignItems: 'flex-end',
                    gap: 2, background: couleurs.surfaceDouce, borderRadius: rayon.sm,
                    padding: 2 }}>
        {barres.map(([ton, valeur]) => (
          <div key={ton} style={{
            flex: 1, height: `${max > 0 ? Math.min(100, (valeur / max) * 100) : 0}%`,
            background: couleurs[ton], borderRadius: `${rayon.sm}px ${rayon.sm}px 0 0`,
            minHeight: valeur > 0 ? 2 : 0, transition: 'height 400ms ease',
          }} />
        ))}
      </div>
      <span style={{ fontSize: 10, color: couleurs.encreFaible,
                     fontVariantNumeric: 'tabular-nums' }}>{heure}</span>
    </div>
  );
}

/* Un bloc de statistiques.

   Corrigé le 2026-08-29 après retour d'usage : « tout se mélange
   visuellement ». La page alternait des sections encadrées et des
   sections posées à même le fond — l'œil ne trouvait donc aucune
   frontière fiable, et seize indicateurs se lisaient comme un mur.

   Désormais chaque bloc a le même cadre, le même en-tête, la même
   séparation. La régularité est ce qui rend une page lisible, bien
   plus que la beauté de chaque élément pris isolément. */
function Bloc({ titre, intro, children, ton }) {
  return (
    <section style={{
      ...carteStyle, padding: 0, marginBottom: espace.xl, overflow: 'hidden',
      borderColor: ton ? couleurs[ton] : couleurs.bord,
    }}>
      <div style={{
        padding: `${espace.md}px ${espace.lg}px`,
        borderBottom: `1px solid ${couleurs.bord}`,
        background: couleurs.surfaceDouce,
      }}>
        <h2 style={{ ...texte.section, margin: 0 }}>{titre}</h2>
        {intro && <p style={{ ...texte.aide, margin: `3px 0 0` }}>{intro}</p>}
      </div>
      <div style={{ padding: espace.lg }}>{children}</div>
    </section>
  );
}

/* Une rangée de chiffres, séparés par des filets verticaux.

   Remplace les cartes flottantes : à l'intérieur d'un bloc déjà
   encadré, des cartes dans des cartes brouillent la lecture. Un filet
   suffit à séparer deux valeurs, et l'œil suit la ligne. */
function Chiffres({ items }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
      {items.map((it, i) => (
        <div key={it.etiquette} style={{
          flex: '1 1 170px', minWidth: 150,
          padding: `0 ${espace.lg}px`,
          borderLeft: i === 0 ? 'none' : `1px solid ${couleurs.bord}`,
        }}>
          <p style={{ ...texte.etiquette, margin: `0 0 2px` }}>{it.etiquette}</p>
          <p style={{
            ...texte.metrique, fontSize: 26, margin: 0,
            color: it.ton ? couleurs[it.ton] : couleurs.encre,
          }}>{it.valeur ?? '—'}</p>
          {it.contexte && (
            <p style={{ fontSize: 12, color: couleurs.encreFaible, margin: '2px 0 0',
                        lineHeight: 1.4 }}>{it.contexte}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function Legende({ items }) {
  return (
    <div style={{ display: 'flex', gap: espace.lg, flexWrap: 'wrap',
                  marginTop: espace.md, fontSize: 12, color: couleurs.encreDouce }}>
      {items.map(([ton, libelle]) => (
        <span key={libelle} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 9, height: 9, borderRadius: 2, flex: 'none',
                         background: couleurs[ton] }} />
          {libelle}
        </span>
      ))}
    </div>
  );
}

/* ─── FIABILITÉ ─────────────────────────────────────────────────────
   « Est-ce qu'on est fiables ? » — la première question d'un
   organisateur, et jusqu'ici la seule à laquelle cette console ne
   savait pas répondre autrement qu'en promettant.

   Le reste de la page montre l'ACTIVITÉ : combien de gobelets, combien
   d'euros, à quelle heure. Cette partie-ci montre la TENUE DES
   ENGAGEMENTS : l'argent promis est-il parti, par quelle porte le
   gobelet est revenu, ce que le système a rattrapé tout seul.

   Chaque bloc s'affiche même quand il n'a rien à dire, et dit alors
   pourquoi. Un bloc qui disparaît est indiscernable d'une panne —
   la leçon du 2026-08-29, déjà apprise plus haut dans cette page. */

/* Un délai en secondes, écrit comme on le dirait à voix haute.
   `duree()` plus haut s'arrête aux minutes : « 187 min 12 s » pour
   trois heures d'attente ne se lit pas. */
function delai(s) {
  if (s === null || s === undefined) return '—';
  if (s < 60) return `${Math.round(s)} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  const h = Math.floor(s / 3600);
  return `${h} h ${String(Math.round((s % 3600) / 60)).padStart(2, '0')}`;
}

function Fiabilite({ f }) {
  if (!f) return null;

  const r  = f.remboursements || {};
  const p  = f.portes         || {};
  const w  = f.webhooks       || {};
  const sc = f.scan           || {};

  const pc  = (n, d) => (d > 0 ? Math.round((n / d) * 100) : null);
  const pct = (n, d) => { const v = pc(n, d); return v === null ? '—' : `${v} %`; };

  const rendus    = (p.au_bac || 0) + (p.automatique || 0) + (p.cloture_barman || 0);
  const en_cours  = (r.jamais_tentes || 0) + (r.en_relance || 0);
  const tauxRemb  = pc(r.succes, r.total);

  return (
    <>
      <div style={styles.separateurPartie}>
        <span style={styles.separateurLabel}>Fiabilité</span>
        <span style={styles.separateurTrait} aria-hidden="true" />
      </div>

      {/* ── A. L'engagement financier ─────────────────────────────── */}
      <Bloc titre="La promesse tenue"
            intro="Une consigne rendue doit être remboursée au client. Ce bloc dit si elle l'a été — et ce qu'il a fallu pour y arriver."
            ton={(r.abandonnes || 0) > 0 ? 'danger' : undefined}>
        {!r.total ? (
          <p style={styles.vide}>
            Aucun remboursement à déclarer pour l'instant. Ce chiffre se remplit
            dès qu'un gobelet issu d'une vente partenaire revient au bac.
          </p>
        ) : (
          <>
            <Chiffres items={[
              { etiquette: 'Remboursements aboutis',
                valeur: pct(r.succes, r.total),
                contexte: `${r.succes} sur ${r.total} dus`,
                ton: tauxRemb === 100 ? 'succes'
                   : tauxRemb !== null && tauxRemb < 95 ? 'danger' : undefined },
              { etiquette: 'Du premier coup',
                valeur: pct(r.premier_coup, r.total),
                contexte: 'partis sans aucune relance' },
              { etiquette: 'Rattrapés par la file',
                valeur: r.sauves_par_relance,
                contexte: (r.sauves_par_relance || 0) > 0
                  ? `jusqu'à ${r.relances_max} tentatives — autant de clients qui n'auraient rien reçu`
                  : 'aucune relance nécessaire',
                ton: (r.sauves_par_relance || 0) > 0 ? 'succes' : undefined },
              { etiquette: 'Encore en cours',
                valeur: en_cours,
                contexte: r.attente_max_s
                  ? `le plus ancien attend depuis ${delai(r.attente_max_s)}`
                  : 'rien en attente',
                ton: en_cours > 0 ? 'alerte' : undefined },
            ]} />

            {(r.abandonnes || 0) > 0 && (
              <p style={{ ...texte.aide, ...bandeau('danger'), marginBottom: 0,
                          marginTop: espace.lg }}>
                <strong>{r.abandonnes} remboursement{r.abandonnes > 1 ? 's' : ''} abandonné
                {r.abandonnes > 1 ? 's' : ''}.</strong> Le partenaire les a refusés
                pour une raison qui ne se corrigera pas seule (requête malformée,
                signature invalide, incohérence). Ils ne repartiront pas tant que
                la cause n'est pas traitée — le bouton « Relancer » les reprendra ensuite.
              </p>
            )}
            {(r.incertains || 0) > 0 && (
              <p style={{ ...texte.aide, ...bandeau('alerte'), marginBottom: 0,
                          marginTop: espace.md }}>
                <strong>{r.incertains} à l'issue incertaine.</strong> L'appel est parti,
                la réponse ne nous est jamais parvenue. Nous ne relançons pas
                automatiquement : le partenaire n'offrant aucune clé d'idempotence,
                un second appel risquerait de créditer le client deux fois.
              </p>
            )}
            <p style={{ ...texte.aide, marginTop: espace.lg, marginBottom: 0 }}>
              Le délai exact entre le dépôt au bac et la confirmation n'est pas mesuré :
              la base ne conserve pas de date d'aboutissement. Le nombre de tentatives
              dit la même chose autrement — passé du premier coup, ou rattrapé.
            </p>
          </>
        )}
      </Bloc>

      {/* ── B. La décomposition du taux de retour ─────────────────── */}
      <Bloc titre="Par quelle porte le gobelet est revenu"
            intro="Le taux de retour ne dit pas tout : un gobelet « rendu » peut n'avoir jamais été posé sur un bac. Voici la répartition réelle.">
        {rendus === 0 ? (
          <p style={styles.vide}>Aucun gobelet rendu pour l'instant.</p>
        ) : (
          <>
            <Chiffres items={[
              { etiquette: 'Scanné au bac',
                valeur: pct(p.au_bac, rendus),
                contexte: `${p.au_bac} gobelets — le retour réel`,
                ton: 'succes' },
              { etiquette: 'Remboursement automatique',
                valeur: pct(p.automatique, rendus),
                contexte: `${p.automatique} consignes — vente jamais clôturée`,
                ton: (p.automatique || 0) > 0 ? 'alerte' : undefined },
              { etiquette: 'Clôture du barman',
                valeur: pct(p.cloture_barman, rendus),
                contexte: `${p.cloture_barman} consignes — bouton « Scan oublié »` },
            ]} />
            <p style={{ ...texte.aide, marginTop: espace.lg, marginBottom: 0 }}>
              Les deux dernières colonnes ne sont pas des échecs : ce sont des
              consignes remboursées <strong>sans</strong> que le gobelet revienne.
              C'est la protection du client — il ne perd jamais une consigne payée,
              même quand personne n'a fermé la vente. Le gobelet, lui, reste dehors.
            </p>
          </>
        )}
      </Bloc>

      {/* ── C. Ce que le réseau a coûté ───────────────────────────── */}
      <Bloc titre="Ce que le réseau a coûté"
            intro="Une coupure entre le terminal de paiement et nous ne doit rien faire perdre. Le partenaire rejoue son webhook jusqu'à ce qu'on l'accepte : voici combien de fois ça a servi.">
        {!w.total ? (
          <p style={styles.vide}>
            Aucun webhook reçu pour ce festival. Le filet de sécurité n'a pas
            encore eu l'occasion de servir.
          </p>
        ) : (
          <>
            <Chiffres items={[
              { etiquette: 'Webhooks reçus', valeur: w.total,
                contexte: 'une vente confirmée par leur serveur' },
              { etiquette: 'Rejoués avant acceptation',
                valeur: pct(w.rejoues, w.total),
                contexte: (w.rejoues || 0) > 0
                  ? `${w.rejoues} ventes — jusqu'à ${w.rejeux_max} rappels`
                  : 'aucun rejeu nécessaire',
                ton: (w.rejoues || 0) > 0 ? 'succes' : undefined },
              { etiquette: 'Arrivés en retard',
                valeur: w.arrives_en_retard,
                contexte: 'plus d’une minute après l’encaissement' },
              { etiquette: 'Retard maximal',
                valeur: delai(w.retard_max_s),
                contexte: 'entre l’encaissement et notre réception' },
            ]} />
            <p style={{ ...texte.aide, marginTop: espace.lg, marginBottom: 0 }}>
              Un webhook rejoué est une bonne nouvelle : la vente aurait été perdue
              sans lui. Aucune vente confirmée par ce canal n'a jamais été abandonnée.
            </p>
          </>
        )}
      </Bloc>

      {/* ── D. La fiabilité vue du comptoir ───────────────────────── */}
      <Bloc titre="Le compte tombe-t-il juste ?"
            intro="La question du barman, et la première inquiétude d'un organisateur : le lecteur voit-il tous les gobelets, du premier coup ?">
        {!sc.commandes ? (
          <p style={styles.vide}>
            Aucune vente close par le barman pour l'instant. Ce chiffre ne
            compte que celles-là : sur une vente fermée d'office, les gobelets
            scannés sont abandonnés et le compte retomberait à zéro sans que
            le lecteur y soit pour quelque chose.
          </p>
        ) : (
          <>
            <Chiffres items={[
              { etiquette: 'Compte exact',
                valeur: pct(sc.compte_juste, sc.commandes),
                contexte: `${sc.compte_juste} commandes sur ${sc.commandes}`,
                ton: pc(sc.compte_juste, sc.commandes) >= 95 ? 'succes'
                   : pc(sc.compte_juste, sc.commandes) < 85 ? 'danger' : 'alerte' },
              { etiquette: 'Scan partiel',
                valeur: sc.partielles,
                contexte: 'des gobelets manquaient à l’appel',
                ton: (sc.partielles || 0) > 0 ? 'alerte' : undefined },
              { etiquette: 'Aucun scan',
                valeur: sc.aucun_scan,
                contexte: 'vente encaissée, aucun gobelet posé' },
            ]} />
            <Barre pct={pc(sc.compte_juste, sc.commandes) || 0} ton="succes" hauteur={8} />
            <p style={{ ...texte.aide, marginTop: espace.md, marginBottom: 0 }}>
              Un taux qui baisse désigne un lecteur à recalibrer, pas un barman à
              former. Croisez-le avec le tableau des pertes par bar pour savoir lequel.
              Seules les <strong>{sc.commandes} ventes closes par le barman</strong> sont
              comptées : ailleurs, les gobelets scannés sont abandonnés à la clôture et
              le compte ne dirait rien du lecteur.
            </p>
          </>
        )}
      </Bloc>
    </>
  );
}

function Stats({ festivalId }) {
  const [ventesBar, setVentesBar] = useState([]);
  const [bilan, setBilan]         = useState(null);
  const [fiabilite, setFiabilite] = useState(null);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    if (!festivalId) return;
    const charger = async () => {
      try {
        /* La fiabilité est demandée À PART, et son échec est avalé.

           Les trois appels ont d'abord partagé un seul `Promise.all` :
           une erreur sur n'importe lequel faisait tomber tout le bloc,
           et la page entière restait blanche. Or le serveur et la
           console se déploient séparément — entre les deux, cette
           route peut ne pas encore exister et répondre 404. Perdre le
           bilan complet d'un festival pour un bloc absent serait un
           mauvais marché. */
        const [barR, bilanR] = await Promise.all([
          api.get(`/admin/stats/ventes-par-bar/${festivalId}`),
          api.get(`/admin/stats/bilan/${festivalId}`),
        ]);
        setVentesBar(barR.data);
        setBilan(bilanR.data);
        try {
          setFiabilite((await api.get(`/admin/stats/fiabilite/${festivalId}`)).data);
        } catch (e) {
          console.error('Indicateurs de fiabilité indisponibles', e);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    charger();
    const i = setInterval(charger, 10000);
    return () => clearInterval(i);
  }, [festivalId]);

  if (loading) return <div style={styles.loading}>Chargement...</div>;

  const totalVentes = ventesBar.reduce((s, b) => s + b.total_ventes, 0);
  const s = bilan?.synthese || {};
  const flux = bilan?.flux || [];
  // Le maximum des TROIS séries, pas des seules sorties : en fin de
  // nuit il n'y a plus de ventes mais beaucoup de retours, et les
  // barres sortaient du cadre.
  const maxFlux = flux.length
    ? Math.max(...flux.flatMap(h => [h.sorties || 0, h.retours || 0, h.pertes || 0]), 1)
    : 1;
  const pertesBar = (bilan?.pertes_par_bar || []).filter(b => b.sorties > 0);
  const a = bilan?.anomalies || {};
  const service = bilan?.service || {};
  const parc = bilan?.parc || {};
  const dehors = bilan?.temps_dehors || {};
  /* Les bars où un bouton a réellement été touché.

     Le bloc était MASQUÉ quand cette liste était vide — mauvaise idée,
     corrigée le 2026-08-29 après retour d'usage : « la section ne
     s'affiche pas ». Un bloc invisible est indiscernable d'une panne.
     On l'affiche toujours, et quand il n'y a rien à montrer il dit
     pourquoi. Un vide qui s'explique vaut mieux qu'un vide. */
  const boutons = (bilan?.boutons?.par_bar || [])
    .filter(b => (b.scan_oublie + b.annule + b.effacer) > 0);

  /* L'heure la plus chargée, nommée. Un maximum dans un graphique se
     repère mal ; écrit en toutes lettres, il se retient — et c'est
     l'information qu'on utilise pour placer le personnel l'an
     prochain. */
  const pic = flux.reduce((m, h) => (!m || h.sorties > m.sorties ? h : m), null);

  /* Le taux de retour est LE chiffre du dispositif : il dit si les
     gobelets reviennent. Les seuils viennent du métier, pas d'une
     norme — en dessous de 80 %, il faut chercher pourquoi. */
  const tonRetour = s.taux_retour == null ? undefined
    : s.taux_retour >= 90 ? 'succes' : s.taux_retour >= 80 ? 'alerte' : 'danger';

  return (
    <div className="jums-page" style={styles.container}>
      <header style={styles.enTete}>
        <h1 style={styles.titre}>Statistiques</h1>
        <span style={styles.rafraichi}>Actualisé toutes les 10 s</span>
      </header>

      <Bloc titre="L'essentiel"
            intro="Les quatre chiffres qui résument la soirée."
            ton={tonRetour === 'danger' ? 'danger' : undefined}>
        <Chiffres items={[
          { etiquette: 'Taux de retour',
            valeur: s.taux_retour == null ? '—' : `${s.taux_retour} %`,
            contexte: `${s.gobelets_rendus ?? 0} rendus sur ${s.gobelets_sortis ?? 0} sortis`,
            ton: tonRetour },
          { etiquette: 'Encore dehors',
            valeur: Math.max(0, (s.gobelets_sortis ?? 0) - (s.gobelets_rendus ?? 0)),
            contexte: 'gobelets pas revenus au bac' },
          { etiquette: 'Consignes encaissées',
            valeur: euros(s.encaisse_eur),
            contexte: `${euros(s.rendu_au_bac_eur)} rendus au bac` },
          { etiquette: 'Perdu par erreur',
            valeur: euros(s.perdu_eur),
            contexte: `${s.rembourses_sans_retour ?? 0} pannes · ${s.clotures_barman ?? 0} clôtures`,
            ton: (s.perdu_eur || 0) > 0 ? 'alerte' : undefined },
        ]} />
        <p style={{ ...texte.aide, marginTop: espace.lg, marginBottom: 0 }}>
          « Perdu par erreur » = consignes remboursées <strong>sans</strong> que le gobelet
          revienne. Le client est remboursé, le gobelet reste dehors : la double perte.
        </p>
      </Bloc>

      <Bloc titre="Le flux de la nuit"
            intro="Trois flux indépendants, heure par heure. Les retours de minuit sont les ventes de 22 h : un gobelet met en moyenne une à deux heures à revenir.">
        {flux.length === 0 ? (
          <p style={styles.vide}>Aucune donnée disponible.</p>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, overflowX: 'auto' }}>
              {flux.map((h, i) => <ColonneFlux key={i} h={h} max={maxFlux} />)}
            </div>
            <Legende items={[['accent', 'servis au bar'], ['succes', 'rendus au bac'],
                             ['danger', 'perdus — remboursés sans retour']]} />
            {pic && pic.sorties > 0 && (
              <p style={{ ...bandeau('accent'), marginTop: espace.lg, marginBottom: 0 }}>
                <strong>Pic d'affluence à {(pic.heure || '').slice(11, 16)}</strong> —
                {' '}{pic.sorties} gobelets servis dans l'heure, soit
                {' '}{Math.max(1, Math.round(pic.sorties / 60))} par minute sur l'ensemble du site.
              </p>
            )}
          </>
        )}
      </Bloc>

      {pertesBar.length > 0 && (
        <Bloc titre="Où l'argent se perd"
              intro="Un bar qui perd nettement plus que ses voisins a presque toujours un problème matériel — lecteur mal réglé, câble, alimentation — et non un problème de barman.">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
              <thead><tr>
                <th style={tableau.th}>Bar</th>
                <th style={{ ...tableau.th, textAlign: 'right' }}>Servis</th>
                <th style={{ ...tableau.th, textAlign: 'right' }}>Pannes</th>
                <th style={{ ...tableau.th, textAlign: 'right' }}>Clôtures</th>
                <th style={{ ...tableau.th, textAlign: 'right' }}>Perdu</th>
                <th style={{ ...tableau.th, textAlign: 'right' }}>Taux</th>
              </tr></thead>
              <tbody>
                {pertesBar.map(b => {
                  const perdus = (b.pannes || 0) + (b.clotures_barman || 0);
                  const taux = b.sorties > 0 ? (perdus / b.sorties) * 100 : 0;
                  const nb = { ...tableau.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
                  return (
                    <tr key={b.bar}>
                      <td style={tableau.td}>{b.bar}</td>
                      <td style={nb}>{b.sorties}</td>
                      <td style={nb}>{b.pannes}</td>
                      <td style={nb}>{b.clotures_barman}</td>
                      <td style={nb}>{euros(b.perdu)}</td>
                      <td style={{ ...tableau.td, textAlign: 'right' }}>
                        <span style={badge(taux >= 10 ? 'danger' : taux >= 5 ? 'alerte' : 'succes')}>
                          {taux.toFixed(1)} %
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Bloc>
      )}

      <Bloc titre="Le parc de gobelets"
            intro="La rotation est le chiffre qui justifie la consigne, et celui qui dit combien de gobelets acheter l'an prochain.">
        <Chiffres items={[
          { etiquette: 'Rotations par gobelet', valeur: parc.rotations ?? '—',
            contexte: 'nombre de services par verre' },
          { etiquette: 'Jetables évités', valeur: s.gobelets_sortis ?? 0,
            contexte: `avec ${parc.utilises ?? 0} gobelets réutilisables` },
          { etiquette: 'Parc mobilisé',
            valeur: parc.total ? `${Math.round((parc.utilises / parc.total) * 100)} %` : '—',
            contexte: `${parc.utilises ?? 0} utilisés sur ${parc.total ?? 0} encodés` },
          { etiquette: 'Gobelets par commande', valeur: parc.gobelets_par_commande ?? '—',
            contexte: `${parc.commandes ?? 0} commandes servies` },
        ]} />
      </Bloc>

      <Bloc titre="Les temps"
            intro="Combien de temps prend un service, et combien de temps un gobelet reste dehors.">
        <Chiffres items={[
          { etiquette: 'Service (médiane)', valeur: duree(service.mediane_s),
            contexte: `ouverture → fermeture du widget · ${service.commandes ?? 0} commandes` },
          { etiquette: 'Service (moyenne)', valeur: duree(service.moyenne_s),
            contexte: 'tirée vers le haut par les cas rares' },
          { etiquette: 'Gobelet dehors', valeur: duree(dehors.mediane_s),
            contexte: `de la vente au retour · ${dehors.gobelets ?? 0} suivis` },
          { etiquette: 'Durée d’activité', valeur: heures(parc.premiere_vente, parc.derniere_vente),
            contexte: plage(parc.premiere_vente, parc.derniere_vente) },
        ]} />
        {(service.par_heure || []).length > 1 && (
          <>
            <p style={{ ...texte.aide, marginTop: espace.xl, marginBottom: espace.sm }}>
              <strong>Temps de service heure par heure.</strong> Un bar dont le temps double
              après minuit dit quelque chose : affluence, fatigue, ou lecteur qui faiblit.
            </p>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, overflowX: 'auto' }}>
              {service.par_heure.map((h, i) => {
                const maxS = Math.max(...service.par_heure.map(x => x.moyenne_s || 0), 1);
                return (
                  <div key={i} style={{ flex: '1 0 40px', display: 'flex', flexDirection: 'column',
                                        alignItems: 'center', gap: 4 }}
                       title={`${(h.heure || '').slice(11, 16)} — ${duree(h.moyenne_s)} sur ${h.commandes} commandes`}>
                    <span style={{ fontSize: 10, color: couleurs.encreDouce,
                                   fontVariantNumeric: 'tabular-nums' }}>
                      {Math.round(h.moyenne_s || 0)}s
                    </span>
                    <div style={{ width: '100%', height: 90, background: couleurs.surfaceDouce,
                                  borderRadius: rayon.sm, display: 'flex',
                                  alignItems: 'flex-end', overflow: 'hidden' }}>
                      <div style={{ width: '100%', height: `${((h.moyenne_s || 0) / maxS) * 100}%`,
                                    background: couleurs.accent, minHeight: 2,
                                    transition: 'height 400ms ease' }} />
                    </div>
                    <span style={{ fontSize: 10, color: couleurs.encreFaible,
                                   fontVariantNumeric: 'tabular-nums' }}>
                      {(h.heure || '').slice(11, 16)}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Bloc>

      <Bloc titre="Ce que fait le barman"
            intro="Les appuis rapportés au nombre de commandes. C'est le taux qui parle, pas le compte : vingt « scan oublié » sur mille commandes est normal, vingt sur soixante veut dire qu'un lecteur ne lit plus.">
        {boutons.length === 0 ? (
          <p style={styles.vide}>
            <strong>Aucun appui enregistré sur ce festival.</strong><br />
            Cette mesure a été mise en service le 29 août 2026 : les festivals antérieurs
            n'en ont pas, et un festival en cours ne remplit ce tableau qu'à partir du
            premier bouton pressé par un barman. Ces chiffres ne remontent pas le temps.
          </p>
        ) : (
          <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
              <thead><tr>
                <th style={tableau.th}>Bar</th>
                <th style={{ ...tableau.th, textAlign: 'right' }}>Commandes</th>
                <th style={{ ...tableau.th, textAlign: 'right' }}>Scan oublié</th>
                <th style={{ ...tableau.th, textAlign: 'right' }}>Annulés</th>
                <th style={{ ...tableau.th, textAlign: 'right' }}>Effacer</th>
                <th style={{ ...tableau.th, textAlign: 'right' }}>Taux scan oublié</th>
              </tr></thead>
              <tbody>
                {boutons.map(b => {
                  const taux = b.commandes > 0 ? (b.scan_oublie / b.commandes) * 100 : 0;
                  const nb = { ...tableau.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
                  return (
                    <tr key={b.bar}>
                      <td style={tableau.td}>{b.bar}</td>
                      <td style={nb}>{b.commandes}</td>
                      <td style={nb}>{b.scan_oublie}</td>
                      <td style={nb}>{b.annule}</td>
                      <td style={nb}>{b.effacer}</td>
                      <td style={{ ...tableau.td, textAlign: 'right' }}>
                        <span style={badge(taux >= 15 ? 'danger' : taux >= 7 ? 'alerte' : 'succes')}>
                          {taux.toFixed(1)} %
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p style={{ ...texte.aide, marginTop: espace.lg, marginBottom: 0 }}>
            « Annulés » compte les fois où le barman a ouvert la confirmation puis renoncé.
            Un chiffre élevé dit qu'il hésite — il ne comprend pas ce que fait le bouton,
            ou n'ose pas s'en servir.
          </p>
          </>
        )}
      </Bloc>

      {/* Par modèle de gobelet (rev25). Le contenant ne suffit pas : un
          25cl et un 50cl partagent souvent le même produit et la même
          consigne. Ce tableau est celui qui décide des achats de l'an
          prochain — un parc où l'on perd trois fois plus de 50cl ne se
          rachète pas à l'identique. */}
      {(bilan?.modeles || []).length > 0 && (
        <Bloc titre="Par modèle de gobelet"
              intro="Ce que devient chaque format. Le taux de retour est le chiffre à suivre : c'est lui qui dit combien racheter, et lesquels.">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
              <thead><tr>
                <th style={tableau.th}>Modèle</th>
                <th style={{ ...tableau.th, textAlign: 'right' }}>Parc</th>
                <th style={{ ...tableau.th, textAlign: 'right' }}>Sortis</th>
                <th style={{ ...tableau.th, textAlign: 'right' }}>Rendus</th>
                <th style={{ ...tableau.th, textAlign: 'right' }}>Perdus</th>
                <th style={{ ...tableau.th, textAlign: 'right' }}>Encore dehors</th>
                <th style={{ ...tableau.th, textAlign: 'right' }}>Taux de retour</th>
              </tr></thead>
              <tbody>
                {bilan.modeles.map(m => {
                  const taux = m.sorties > 0 ? (m.retours / m.sorties) * 100 : null;
                  const nb = { ...tableau.td, textAlign: 'right',
                               fontVariantNumeric: 'tabular-nums' };
                  return (
                    <tr key={m.modele}>
                      <td style={tableau.td}><strong>{m.modele}</strong></td>
                      <td style={nb}>{m.parc}</td>
                      <td style={nb}>{m.sorties}</td>
                      <td style={nb}>{m.retours}</td>
                      <td style={nb}>{m.pertes}</td>
                      <td style={nb}>{m.dehors}</td>
                      <td style={{ ...tableau.td, textAlign: 'right' }}>
                        {taux === null
                          ? <span style={{ color: couleurs.encreFaible }}>—</span>
                          : <span style={badge(taux >= 85 ? 'succes'
                                               : taux >= 65 ? 'alerte' : 'danger')}>
                              {taux.toFixed(1)} %
                            </span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p style={{ ...texte.aide, marginTop: espace.lg, marginBottom: 0 }}>
            Le modèle se renseigne à l'import des puces, dans l'onglet Gobelets.
            Il n'a aucun effet sur le scan — deux modèles d'un même produit se
            valent au comptoir, ils ne se distinguent qu'ici.
          </p>
        </Bloc>
      )}

      <Fiabilite f={fiabilite} />

      {/* Santé de l'intégration partenaire (rev24). Ce bloc existe parce
          que le 31/08/2026 il a fallu fouiller les journaux du serveur
          pendant une heure pour découvrir que la route C n'arrivait
          jamais — l'information était là, invisible. */}
      {(() => {
        const rt = bilan?.routes;
        if (!rt || !rt.total) return null;
        const pc = n => Math.round((n / rt.total) * 100);
        const fermees = rt.fermees_route_c + rt.fermees_echeance + rt.fermees_autrement;
        return (
          <Bloc titre="Routes du partenaire"
                intro="Les trois échanges avec le terminal de paiement : celui qui ouvre le widget, celui qui sécurise la vente, celui qui la clôt. Un chiffre à zéro ici est une panne d'intégration, pas un problème de bar.">

            {rt.b_seule === 0 && rt.a_et_b === 0 && (
              <p style={{ ...texte.aide, ...bandeau('danger'), marginTop: 0 }}>
                <strong>Le webhook n'arrive jamais.</strong> Aucune vente n'a été
                confirmée par la route B. Le filet de sécurité qui rattrape les
                coupures réseau ne fonctionne pas — à signaler au partenaire.
              </p>
            )}
            <h3 style={texte.section}>Comment la vente arrive</h3>
            <Chiffres items={[
              { etiquette: 'Les deux routes', valeur: `${pc(rt.a_et_b)} %`,
                contexte: `${rt.a_et_b} ventes — le cas nominal`,
                ton: rt.a_et_b === 0 ? 'alerte' : 'succes' },
              { etiquette: 'Widget seul', valeur: `${pc(rt.a_seule)} %`,
                contexte: `${rt.a_seule} ventes — le webhook n'est pas venu`,
                ton: rt.a_seule > 0 ? 'alerte' : undefined },
              { etiquette: 'Webhook seul', valeur: `${pc(rt.b_seule)} %`,
                contexte: `${rt.b_seule} ventes — terminal coupé, aucun widget` },
            ]} />

            <h3 style={{ ...texte.section, marginTop: espace.xl }}>Comment elle se clôt</h3>
            {!rt.suivi_depuis ? (
              <p style={styles.vide}>
                <strong>Pas encore de données.</strong><br />
                La fermeture du widget n'est horodatée que depuis la mise en service
                de ce suivi. Ce tableau se remplira dès la première commande close
                par le bouton « Fermer » du terminal — les ventes antérieures ne
                peuvent pas être qualifiées après coup.
              </p>
            ) : (
              <>
                <Chiffres items={[
                  { etiquette: 'Par « Fermer »', valeur: rt.fermees_route_c,
                    contexte: 'gobelets scannés retenus',
                    ton: rt.fermees_route_c > 0 ? 'succes' : undefined },
                  { etiquette: 'Toute seule', valeur: rt.fermees_autrement,
                    contexte: 'vente suivante — scans abandonnés',
                    ton: rt.fermees_autrement > 0 ? 'danger' : undefined },
                  { etiquette: 'À l’échéance', valeur: rt.fermees_echeance,
                    contexte: 'délai dépassé — tout remboursé',
                    ton: rt.fermees_echeance > 0 ? 'alerte' : undefined },
                  { etiquette: 'Encore ouvertes', valeur: rt.encore_ouvertes,
                    contexte: 'en cours de service' },
                ]} />
                <p style={{ ...texte.aide, marginTop: espace.lg, marginBottom: 0 }}>
                  Décompte des ventes postérieures au{' '}
                  {new Date(rt.suivi_depuis).toLocaleString('fr-FR')} — début du suivi.
                  Les deux colonnes du milieu coûtent de l'argent : dans les deux cas
                  les gobelets scannés sont abandonnés et toutes les consignes remboursées.
                </p>
              </>
            )}

            {rt.widget_ouvert?.n > 0 && (
              <>
                <h3 style={{ ...texte.section, marginTop: espace.xl }}>
                  Temps du geste au comptoir
                </h3>
                <Chiffres items={[
                  { etiquette: 'En moyenne', valeur: duree(rt.widget_ouvert.moyenne_s),
                    contexte: `sur ${rt.widget_ouvert.n} ventes closes par « Fermer »` },
                  { etiquette: 'Médiane', valeur: duree(rt.widget_ouvert.mediane_s),
                    contexte: 'la moitié des ventes font mieux' },
                ]} />
                <p style={{ ...texte.aide, marginTop: espace.lg, marginBottom: 0 }}>
                  De l'ouverture du widget à son « Fermer ». Seules les fermetures
                  franches comptent : sur une commande close d'elle-même, cet écart
                  ne mesure pas le geste du barman mais le délai d'échéance.
                </p>

                {/* Heure par heure — même lecture que le temps de service :
                    c'est la PENTE qui informe, pas la valeur absolue. Un geste
                    qui double après minuit dit la fatigue ou l'affluence. */}
                {(rt.widget_par_heure || []).length > 1 && (
                  <>
                    <p style={{ ...texte.aide, marginTop: espace.xl, marginBottom: espace.sm }}>
                      <strong>Heure par heure.</strong> Le temps que le widget reste
                      ouvert au comptoir, moyenne par tranche horaire.
                    </p>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6,
                                  overflowX: 'auto' }}>
                      {rt.widget_par_heure.map((h, i) => {
                        const maxS = Math.max(
                          ...rt.widget_par_heure.map(x => x.moyenne_s || 0), 1);
                        return (
                          <div key={i} style={{ flex: '1 0 40px', display: 'flex',
                                                flexDirection: 'column', alignItems: 'center',
                                                gap: 4 }}
                               title={`${h.heure} h — ${duree(h.moyenne_s)} sur ${h.n} vente(s)`}>
                            <span style={{ fontSize: 10, color: couleurs.encreDouce,
                                           fontVariantNumeric: 'tabular-nums' }}>
                              {Math.round(h.moyenne_s || 0)}s
                            </span>
                            <div style={{ width: '100%', height: 90,
                                          background: couleurs.surfaceDouce,
                                          borderRadius: rayon.sm, display: 'flex',
                                          alignItems: 'flex-end', overflow: 'hidden' }}>
                              <div style={{ width: '100%',
                                            height: `${((h.moyenne_s || 0) / maxS) * 100}%`,
                                            background: couleurs.accent, minHeight: 2,
                                            transition: 'height 400ms ease' }} />
                            </div>
                            <span style={{ fontSize: 10, color: couleurs.encreFaible,
                                           fontVariantNumeric: 'tabular-nums' }}>
                              {h.heure} h
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </>
            )}
          </Bloc>
        );
      })()}

      <Bloc titre="Anomalies"
            intro="Ce qui a mal tourné, et ce que ça a coûté.">
        <Chiffres items={[
          { etiquette: 'Lus en trop', valeur: a.gobelets_en_trop ?? 0,
            contexte: 'lecteur qui capte hors du tapis',
            ton: (a.gobelets_en_trop || 0) > 0 ? 'alerte' : undefined },
          { etiquette: 'Clôtures à zéro', valeur: a.closes_sans_scan ?? 0,
            contexte: 'consigne rendue, gobelet jamais sorti' },
          { etiquette: 'Commandes partielles', valeur: a.partiellement_scannees ?? 0,
            contexte: 'moins de gobelets scannés que vendus',
            ton: (a.partiellement_scannees || 0) > 0 ? 'alerte' : undefined },
          { etiquette: 'Expirées', valeur: a.expirees ?? 0,
            contexte: 'délai dépassé sans validation',
            ton: (a.expirees || 0) > 0 ? 'alerte' : undefined },
        ]} />
      </Bloc>

      {(bilan?.bacs || []).length > 0 && (
        <Bloc titre="Les bacs de retour"
              intro="Remplissage depuis la dernière vidange.">
          <div style={{ display: 'flex', flexDirection: 'column', gap: espace.lg }}>
            {bilan.bacs.map(b => {
              const remplissage = b.capacite_max
                ? Math.round(((b.compteur_vidange || 0) / b.capacite_max) * 100) : null;
              return (
                <div key={b.nom}>
                  <div style={styles.ligneEntete}>
                    <h3 style={texte.section}>{b.nom}</h3>
                    <span style={styles.chiffreLigne}>
                      {b.retours ?? 0}<span style={styles.unite}> gobelets rendus</span>
                    </span>
                  </div>
                  {remplissage !== null ? (
                    <>
                      <Barre pct={remplissage}
                        ton={remplissage >= 90 ? 'danger' : remplissage >= 70 ? 'alerte' : 'succes'} />
                      <p style={{ ...texte.aide, marginTop: espace.xs, marginBottom: 0 }}>
                        {b.compteur_vidange ?? 0} depuis la dernière vidange sur {b.capacite_max}
                        {' '}— {remplissage} % plein
                        {remplissage >= 90 && <strong> · à vider</strong>}
                      </p>
                    </>
                  ) : (
                    <p style={{ ...texte.aide, margin: 0 }}>
                      Capacité non renseignée — impossible d'alerter avant le débordement.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </Bloc>
      )}

      {(bilan?.produits || []).length > 0 && (
        <Bloc titre="Gobelets par produit" intro="Ce qui se vend, et dans quelles proportions.">
          <div style={{ display: 'flex', flexDirection: 'column', gap: espace.md }}>
            {bilan.produits.map(p => {
              const part = s.gobelets_sortis > 0
                ? Math.round((p.gobelets / s.gobelets_sortis) * 100) : 0;
              return (
                <div key={p.produit}>
                  <div style={styles.ligneEntete}>
                    <h3 style={texte.section}>{p.produit}</h3>
                    <span style={styles.chiffreLigne}>
                      {p.gobelets}<span style={styles.unite}> gobelets · {part} %</span>
                    </span>
                  </div>
                  <Barre pct={part} hauteur={6} />
                </div>
              );
            })}
          </div>
        </Bloc>
      )}

      <Bloc titre="Gobelets servis par bar" intro="Avec le détail de chaque tapis.">
        {ventesBar.length === 0 ? (
          <p style={styles.vide}>Aucune vente enregistrée pour l'instant.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: espace.xl }}>
            {ventesBar.map(bar => {
              const part = totalVentes > 0 ? Math.round((bar.total_ventes / totalVentes) * 100) : 0;
              return (
                <div key={bar.bar_id}>
                  <div style={styles.ligneEntete}>
                    <h3 style={texte.section}>{bar.bar_nom}</h3>
                    <span style={styles.chiffreLigne}>
                      {bar.total_ventes}
                      <span style={styles.unite}> gobelets · {part} %</span>
                    </span>
                  </div>
                  <Barre pct={part} hauteur={6} />
                  {bar.tapis.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column',
                                  gap: 5, marginTop: espace.md }}>
                      {bar.tapis.map(t => (
                        <div key={t.tapis_id} style={{ display: 'flex', alignItems: 'center', gap: espace.md }}>
                          <span style={{ ...texte.mono, color: couleurs.encreDouce,
                                         minWidth: 130, flex: 'none' }}>{t.identifiant}</span>
                          <div style={{ flex: 1 }}>
                            <Barre pct={bar.total_ventes > 0
                              ? Math.round((t.total_ventes / bar.total_ventes) * 100) : 0}
                              ton="bordFort" hauteur={4} />
                          </div>
                          <span style={{ ...styles.chiffreLigne, fontSize: 12.5,
                                         minWidth: 46, textAlign: 'right' }}>
                            {t.total_ventes}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Bloc>
    </div>
  );
}

export { Stats };

/* Les appels reçus du partenaire pour UNE vente. (rev26)

   Ce panneau ne reconstruit rien : il affiche ce que le serveur a
   gardé, mot pour mot. Une route absente est annoncée comme telle
   plutôt que devinée — c'est justement l'écart entre ce qu'on attendait
   et ce qui est arrivé qu'on vient chercher ici. */
function Echanges({ donnees, chargement, erreur, onClose }) {
  /* L'URL COMPLÈTE, telle qu'on la collerait dans un navigateur.

     Le serveur ne stocke que la chaîne de requête — le domaine et le
     chemin, eux, ne sont pas des données du partenaire : ils sont
     déterminés par la route qui a reçu l'appel (/consigne pour A,
     /fermewidget pour C) et par l'adresse de notre propre API. Les
     recomposer ici n'invente donc rien, et fait profiter du même
     affichage les ventes déjà enregistrées avant ce changement.

     La base vient de la configuration réelle de la console — jamais
     d'une valeur écrite en dur, qui finirait par diverger. */
  const base = (api.defaults.baseURL || '').replace(/\/+$/, '');
  const urlComplete = (chemin, requete) => {
    if (!requete) return null;
    // Déjà absolue (au cas où un futur enregistrement le serait) : on n'y
    // touche pas.
    if (/^https?:\/\//i.test(requete)) return requete;
    return `${base}${chemin}?${requete}`;
  };

  const bloc = (lettre, titre, contenu, recue, extra) => (
    <div key={lettre} style={{ marginBottom: espace.lg }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: espace.sm,
                    marginBottom: espace.xs }}>
        <span style={badge(contenu ? 'succes' : 'neutre')}>Route {lettre}</span>
        <strong style={{ fontSize: 14 }}>{titre}</strong>
        <span style={{ ...texte.aide, margin: 0 }}>
          {recue
            ? `reçue à ${new Date(recue).toLocaleString('fr-FR')}${extra || ''}`
            : 'jamais reçue'}
        </span>
      </div>
      {contenu && (
        <pre style={{
          margin: 0, padding: espace.md, background: couleurs.surfaceDouce,
          border: `1px solid ${couleurs.bord}`, borderRadius: rayon.md,
          fontSize: 11.5, lineHeight: 1.5, whiteSpace: 'pre-wrap',
          wordBreak: 'break-all', maxHeight: 220, overflowY: 'auto',
        }}>{contenu}</pre>
      )}
    </div>
  );

  return (
    <div onClick={onClose} className="jums-modale-voile"
         style={{ position: 'fixed', inset: 0, background: couleurs.voile,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: espace.lg, zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} className="jums-modale"
           style={{ ...carteStyle, maxWidth: 760, width: '100%', maxHeight: '82vh',
                    overflowY: 'auto', padding: espace.xl }}>
        <div style={styles.ligneEntete}>
          <h2 style={{ ...texte.section, margin: 0 }}>Échanges avec le partenaire</h2>
          <button onClick={onClose} style={bouton('discret')}>Fermer</button>
        </div>

        {chargement && <p style={styles.vide}>Chargement…</p>}
        {erreur && <p style={{ ...bandeau('danger'), padding: espace.md }}>{erreur}</p>}

        {donnees && (
          <>
            <p style={{ ...texte.aide, marginTop: espace.sm }}>
              Vente <code>{donnees.transaction}</code>
              {donnees.terminal && <> · terminal <code>{donnees.terminal}</code></>}
              {donnees.total_verres != null && <> · {donnees.total_verres} gobelet(s)</>}
            </p>

            {bloc('A', 'Ouverture du widget',
                  urlComplete('/consigne', donnees.a?.url), donnees.a?.recue_at)}
            {/* Reçu en GET : l'URL, comme A et C. Reçu en POST : le corps,
                parce qu'il n'y a alors AUCUNE URL — les champs du corps
                sont le message. Montrer une adresse reconstituée dans ce
                cas serait inventer ce qu'on prétend avoir reçu. */}
            {bloc('B',
                  donnees.b?.url ? 'Webhook de paiement (reçu en GET)'
                                 : 'Webhook de paiement (reçu en POST)',
                  donnees.b
                    ? (donnees.b.url
                        ? urlComplete('/webhook/paiement', donnees.b.url)
                        : JSON.stringify(donnees.b.payload, null, 2))
                    : null,
                  donnees.b?.recue_at,
                  donnees.b?.rejeux ? ` · ${donnees.b.rejeux} rejeu(x)` : '')}
            {bloc('C', 'Fermeture du widget',
                  urlComplete('/fermewidget', donnees.c?.url), donnees.c?.recue_at)}

            <p style={{ ...texte.aide, marginTop: espace.lg, marginBottom: 0 }}>
              Ce sont les appels tels que reçus, signature comprise. Les ventes
              antérieures au 1<sup>er</sup> septembre 2026 n'en ont pas gardé la
              trace : leurs routes s'affichent comme jamais reçues, faute de
              données, et non parce qu'elles ne sont pas arrivées.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default function Dashboard({ festivalId }) {
  const [stock, setStock]           = useState(null);
  const [transactions, setTx]       = useState([]);
  const [bacs, setBacs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  // Panneau des échanges partenaire — chargé au clic seulement (rev26).
  const [echanges, setEchanges]     = useState(null);   // { donnees, chargement, erreur }

  const ouvrirEchanges = async (transactionId) => {
    if (!transactionId) return;
    setEchanges({ chargement: true });
    try {
      const res = await api.get(`/admin/echanges/${encodeURIComponent(transactionId)}`);
      setEchanges({ donnees: res.data });
    } catch (e) {
      setEchanges({ erreur: e.response?.status === 404
        ? "Aucun échange enregistré pour cette vente."
        : (e.response?.data?.detail || "Échanges indisponibles.") });
    }
  };

  const fetchData = async () => {
    if (!festivalId) return;
    try {
      const [sR, tR, bR] = await Promise.all([
        api.get(`/stock/${festivalId}`),
        api.get(`/stock/${festivalId}/transactions?limit=50`),
        api.get(`/admin/bacs-stats/${festivalId}`),
      ]);
      setStock(sR.data); setTx(tR.data); setBacs(bR.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchData();
    const i = setInterval(fetchData, 5000);
    return () => clearInterval(i);
  }, [festivalId]);

  if (!festivalId) return (
    <div className="jums-page" style={styles.container}>
      <p style={bandeau('danger')}>Aucun festival sélectionné.</p>
    </div>
  );
  if (loading) return <div style={styles.loading}>Chargement…</div>;

  const perdus      = stock?.perdus || 0;
  const total       = stock?.total_verres || 0;
  const enCirc      = stock?.en_circulation || 0;
  const stockDispo  = total - enCirc - perdus;
  // Totaux calculés côté serveur sur TOUTE la base (pas la liste tronquée à 50)
  const nbMisEnCirc = stock?.total_mis_en_circ || 0;
  const nbRetours   = stock?.total_retours || 0;
  const tauxRetour  = nbMisEnCirc > 0 ? Math.round((nbRetours / nbMisEnCirc) * 100) : 0;
  const pourcent    = (n) => total > 0 ? `${Math.round((n / total) * 100)} % du parc` : null;

  // Bacs à surveiller : c'est LA seule chose qui demande une action
  // physique immédiate, elle est donc annoncée en haut de page plutôt
  // que découverte en faisant défiler.
  const bacsPleins = bacs.filter(b => (b.compteur_vidange / b.capacite_max) >= 0.9);
  const bacsBientot = bacs.filter(b => {
    const p = b.compteur_vidange / b.capacite_max;
    return p >= 0.7 && p < 0.9;
  });

  return (
    <div className="jums-page" style={styles.container}>
      <header style={styles.enTete}>
        <h1 style={styles.titre}>Vue d'ensemble</h1>
        <span style={styles.rafraichi}>Actualisé toutes les 5 s</span>
      </header>

      {(bacsPleins.length > 0 || perdus > 0) && (
        <div style={{ ...bandeau(bacsPleins.length ? 'danger' : 'alerte'),
                      marginBottom: espace.lg }} role="status">
          {bacsPleins.length > 0 && (
            <>À vider maintenant : <strong>{bacsPleins.map(b => b.nom).join(', ')}</strong>.{' '}</>
          )}
          {perdus > 0 && <>{perdus} gobelet(s) marqué(s) perdus.</>}
        </div>
      )}

      <div style={styles.cartes}>
        <Indicateur etiquette="Parc total"       valeur={total} contexte="gobelets enregistrés" />
        <Indicateur etiquette="Stock disponible" valeur={stockDispo} contexte={pourcent(stockDispo)} />
        <Indicateur etiquette="En circulation"   valeur={enCirc} contexte={pourcent(enCirc)} />
        {perdus > 0 && (
          <Indicateur etiquette="Perdus" valeur={perdus} contexte={pourcent(perdus)} ton="danger" />
        )}
        <Indicateur
          etiquette="Taux de retour" valeur={`${tauxRetour} %`}
          contexte={`${nbRetours} rendus sur ${nbMisEnCirc} servis`}
          ton={nbMisEnCirc > 20 && tauxRetour < 50 ? 'alerte' : undefined} />
      </div>

      {/* BACS — trié du plus plein au moins plein : ce qui demande une
          action se présente en premier, sans avoir à comparer soi-même. */}
      {bacs.length > 0 && (
        <section style={{ marginBottom: espace.xxl }}>
          <h2 style={styles.sectionTitre}>Remplissage des bacs de déconsigne</h2>
          <div style={{ display: 'flex', gap: espace.md, flexWrap: 'wrap' }}>
            {[...bacs]
              .sort((a, b) => (b.compteur_vidange / b.capacite_max) - (a.compteur_vidange / a.capacite_max))
              .map(bac => {
                const pct = Math.min(Math.round((bac.compteur_vidange / bac.capacite_max) * 100), 100);
                const ton = pct >= 90 ? 'danger' : pct >= 70 ? 'alerte' : 'succes';
                return (
                  <div key={bac.id} style={{
                    ...styles.section, flex: 1, minWidth: 250,
                    borderColor: pct >= 90 ? couleurs.danger : couleurs.bord,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between',
                                  alignItems: 'flex-start', gap: espace.sm, marginBottom: espace.md }}>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ ...texte.section, margin: 0 }}>{bac.nom}</p>
                        {bac.localisation && (
                          <p style={{ fontSize: 12, color: couleurs.encreFaible, margin: '2px 0 0' }}>
                            {bac.localisation}
                          </p>
                        )}
                      </div>
                      {pct >= 90 && <span style={badge('danger')}>À vider</span>}
                      {pct >= 70 && pct < 90 && <span style={badge('alerte')}>Bientôt plein</span>}
                    </div>

                    <Barre pct={pct} ton={ton} hauteur={8} />

                    <div style={{ display: 'flex', justifyContent: 'space-between',
                                  alignItems: 'flex-end', gap: espace.sm, marginTop: espace.md }}>
                      <div>
                        <span style={{ fontSize: 13, color: couleurs.encre,
                                       fontVariantNumeric: 'tabular-nums' }}>
                          <strong>{bac.compteur_vidange}</strong>
                          <span style={{ color: couleurs.encreDouce }}> / {bac.capacite_max} · {pct} %</span>
                        </span>
                        <p style={{ fontSize: 11.5, color: couleurs.encreFaible, margin: '3px 0 0',
                                    fontVariantNumeric: 'tabular-nums' }}>
                          {bac.total_retours || 0} retours depuis le début
                        </p>
                      </div>
                      <button
                        style={bouton('discret', 'sm')}
                        onClick={async () => { await api.post(`/admin/bacs/${bac.id}/vidange`); fetchData(); }}
                      >
                        Vidange faite
                      </button>
                    </div>
                    {bac.derniere_vidange && (
                      <p style={{ fontSize: 11.5, color: couleurs.encreFaible, margin: `${espace.sm}px 0 0` }}>
                        Dernière vidange : {new Date(bac.derniere_vidange).toLocaleString('fr-FR')}
                      </p>
                    )}
                  </div>
                );
              })}
          </div>
        </section>
      )}

      <section style={styles.section}>
        <div style={styles.ligneEntete}>
          <h2 style={{ ...texte.section, margin: 0 }}>Transactions récentes</h2>
          <span style={texte.etiquette}>{Math.min(transactions.length, 30)} dernières</span>
        </div>
        {!transactions.length ? (
          <p style={styles.vide}>Aucune transaction.</p>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={tableau.table}>
              <thead>
                <tr>
                  <th style={tableau.th}>Heure</th>
                  <th style={tableau.th}>Vente partenaire</th>
                  <th style={tableau.th}>Événement</th>
                  <th style={tableau.th}>Gobelet</th>
                  <th style={tableau.th}>Bracelet</th>
                  <th style={{ ...tableau.th, textAlign: 'right' }}>Montant</th>
                  <th style={tableau.th}>Remboursement</th>
                  {/* Par quelle route la vente d'origine nous est parvenue.
                      Dernière colonne : c'est une information de diagnostic,
                      elle ne doit pas s'interposer entre l'heure, la vente
                      et le montant, qu'on lit tous les jours. (rev24) */}
                  <th style={tableau.th}>Routes</th>
                </tr>
              </thead>
              <tbody>
                {transactions.slice(0, 30).map((tx, i) => (
                  <tr key={i} style={{ background: i % 2 ? couleurs.surfaceDouce : couleurs.surface }}>
                    <td style={{ ...tableau.td, whiteSpace: 'nowrap',
                                 fontVariantNumeric: 'tabular-nums' }}>
                      {new Date(tx.created_at).toLocaleTimeString('fr-FR')}
                    </td>
                    {/* Identifiant de vente du partenaire : c'est LUI qui relie
                        une ligne à une commande précise (widget, webhook, appel
                        de remboursement). Indispensable pour suivre une vente
                        de bout en bout. `title` expose la valeur entière au
                        survol, les identifiants réels étant des UUID. */}
                    <td style={tableau.tdMono} title={tx.partenaire_transaction_id || ''}>
                      {tx.partenaire_transaction_id || '—'}
                    </td>
                    <td style={tableau.td}>
                      <span style={badge(
                        tx.type === 'mise_en_circulation' ? 'accent'
                        : tx.type === 'retour' ? 'succes'
                        : tx.type === 'scan_oublie' ? 'alerte' : 'neutre'
                      )}>
                        {tx.type === 'mise_en_circulation' ? 'Servi'
                          : tx.type === 'retour' ? 'Rendu'
                          : tx.type === 'scan_oublie' ? 'Scan oublié' : 'Remboursement'}
                      </span>
                    </td>
                    <td style={tableau.tdMono}>{tx.rfid_verre || '—'}</td>
                    <td style={{ ...tableau.tdMono, color: couleurs.encreFaible }}>
                      {tx.rfid_bracelet || '—'}
                    </td>
                    <td style={tableau.tdNombre}>
                      {tx.montant_consigne ? `${Number(tx.montant_consigne).toFixed(2)} €` : '—'}
                    </td>
                    <td style={tableau.td}>
                      {tx.statut_remboursement === 'non_applicable'
                        ? <span style={{ color: couleurs.encreFaible, fontSize: 12.5 }}>—</span>
                        : <span
                            style={badge(
                              tx.statut_remboursement === 'success' ? 'succes'
                              : tx.statut_remboursement === 'en_attente' ? 'alerte'
                              : tx.statut_remboursement === 'echec' ? 'danger'
                              /* « À vérifier » n'est plus produit depuis
                                 le 02/09/2026 : le return_id de la fiche
                                 v3 rend toute relance sans risque. Le
                                 libellé reste pour les lignes d'AVANT,
                                 qui datent d'une époque où personne ne
                                 pouvait savoir si l'appel avait abouti. */
                              : tx.statut_remboursement === 'incertain' ? 'alerte'
                              : 'neutre')}
                            title={tx.statut_remboursement === 'incertain'
                              ? "Ligne antérieure au 02/09/2026 : le partenaire n'avait pas répondu, et rien ne permettait alors de savoir si le retour avait été enregistré. Depuis, chaque déclaration porte un numéro de retour qui rend les relances sans risque."
                              : undefined}>
                            {tx.statut_remboursement === 'success' ? 'Remboursé'
                              : tx.statut_remboursement === 'en_attente' ? 'En attente'
                              : tx.statut_remboursement === 'echec' ? 'Échec'
                              : tx.statut_remboursement === 'incertain' ? 'À vérifier'
                              : tx.statut_remboursement}
                          </span>}
                    </td>
                    {/* Cliquable : la fenêtre montre les appels EXACTS
                        reçus du partenaire, sans rien reconstruire. */}
                    <td style={tableau.td}>
                      {!tx.routes
                        ? <span style={{ color: couleurs.encreFaible, fontSize: 12.5 }}>—</span>
                        : <button
                            onClick={() => ouvrirEchanges(tx.partenaire_transaction_id)}
                            title="Voir les appels reçus du partenaire pour cette vente"
                            style={{
                              ...badge(tx.routes.includes('C') ? 'succes'
                                       : tx.routes === 'B' ? 'alerte' : 'neutre'),
                              border: `1px solid ${couleurs.bord}`, cursor: 'pointer',
                              font: 'inherit', fontSize: 12,
                            }}>
                            {tx.routes}
                          </button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Panneau des échanges partenaire, ouvert au clic sur la colonne
          Routes. Monté en dernier pour recouvrir toute la page. */}
      {echanges && (
        <Echanges donnees={echanges.donnees} chargement={echanges.chargement}
                  erreur={echanges.erreur} onClose={() => setEchanges(null)} />
      )}
    </div>
  );
}

// ─── TERMINAL BARMAN ───────────────────────────────────────

const styles = {
  // 52 px de barre de navigation + 24 de respiration. Largeur bornée à
  // 1440 : au-delà, une ligne de tableau devient trop longue pour que
  // l'œil retrouve la colonne de droite depuis celle de gauche.
  container: {
    minHeight: '100vh', background: couleurs.fond,
    padding: `${52 + espace.xl}px ${espace.xl}px ${espace.xxl}px`,
    maxWidth: 1440, margin: '0 auto',
  },
  loading: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', fontSize: 14, color: couleurs.encreDouce,
  },
  enTete: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    gap: espace.lg, marginBottom: espace.xl,
  },
  titre: { ...texte.titre },
  // « Actualisé toutes les 5 s » est une note de bas de page, pas un
  // sous-titre : discrète, mais présente — elle évite de recharger la
  // page en se demandant si les chiffres sont figés.
  rafraichi: { ...texte.etiquette, color: couleurs.encreFaible },
  cartes: { display: 'flex', gap: espace.md, marginBottom: espace.xxl, flexWrap: 'wrap' },
  carte: {
    ...carteStyle, padding: espace.lg, flex: 1, minWidth: 150,
    display: 'flex', flexDirection: 'column', gap: 3,
  },
  carteTitre: { ...texte.etiquette, margin: 0 },
  carteValeur: { ...texte.metrique },
  carteContexte: { fontSize: 12, color: couleurs.encreFaible, margin: 0 },
  section: { ...carteStyle, padding: espace.lg },
  sectionTitre: { ...texte.section, marginBottom: espace.md, marginTop: 0 },
  ligneEntete: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    gap: espace.md, marginBottom: espace.md,
  },
  chiffreLigne: {
    fontSize: 14, fontWeight: 600, color: couleurs.encre,
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  },
  unite: { fontWeight: 400, color: couleurs.encreDouce, fontSize: 12.5 },
  // 380 px : environ douze lignes visibles, assez pour juger d'un coup
  // d'œil du rythme des ventes sans noyer le reste de la page.
  tableWrapper: { maxHeight: 380, overflowY: 'auto', margin: `0 -${espace.lg}px -${espace.lg}px` },
  vide: { ...texte.aide, textAlign: 'center', padding: espace.xl, margin: 0 },
  /* Séparateur de PARTIE — pas de bloc. La page enchaîne une dizaine de
     blocs identiques ; sans respiration, la partie « Fiabilité » ne se
     distinguerait pas de la précédente et on ne saurait pas qu'on a
     changé de sujet. Un mot et un filet suffisent : ajouter un cadre
     ferait un bloc dans un bloc, ce que cette page évite partout. */
  separateurPartie: {
    display: 'flex', alignItems: 'center', gap: espace.md,
    margin: `${espace.xxl}px 0 ${espace.lg}px`,
  },
  separateurLabel: {
    ...texte.etiquette, color: couleurs.encreDouce, whiteSpace: 'nowrap', margin: 0,
  },
  separateurTrait: { flex: 1, height: 1, background: couleurs.bord },
};
